import { useEffect, useMemo, useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useNow } from "../api/hooks";
import type { LiveMap } from "../api/types";
import { can } from "../auth/roles";
import { StatusBadge } from "../components/Badges";
import { APPLIANCE_STATUS, DISPATCH_STATUS, INCIDENT_STATUS, SEVERITY, SEVERITY_ORDER, statusOf } from "../components/labels";
import { QueryState } from "../components/ProblemAlert";
import { useFireContext } from "../context";
import { formatDateTime, formatSeconds } from "../format/datetime";
import { FireMap } from "../map/FireMap";
import { Legend, type LegendKey } from "../map/Legend";
import { pt, type MapData } from "../map/layers";
import { alertToDispatch, IncidentDetail, IncidentFlags } from "./IncidentDetail";
import { useConfigNames, useStationNames } from "./shared";
import { MapPage } from "../components/MapPage";
import { Dot } from "@lcc/ui";

/** Live map data for the board: incidents, unhandled alerts, refused SOS, stations and appliances with positions. */
export function liveMapData(live: LiveMap | undefined, typeLabel: (k: string) => string, selectedId: string | null, maxAgeS: number): MapData {
  if (!live) return {};
  return {
    selectedId,
    incidents: live.incidents.flatMap((i) => {
      const p = pt(i.lon, i.lat);
      return p ? [{ id: i.incident_id, reference: i.reference, severity: i.severity, status: i.status, point: p, accuracyM: i.accuracy_m, typeLabel: typeLabel(i.incident_type), backup: i.backup_requested }] : [];
    }),
    alerts: live.alerts.flatMap((a) => {
      const p = pt(a.lon, a.lat);
      return p ? [{ id: a.alert_id, reference: a.reference, channel: a.channel, point: p, accuracyM: a.accuracy_m }] : [];
    }),
    refused: live.refused_sos.flatMap((r) => {
      const p = pt(r.lon, r.lat);
      return p ? [{ id: r.refused_id, point: p }] : [];
    }),
    stations: live.stations.flatMap((s) => {
      const p = pt(s.lon, s.lat);
      return p ? [{ id: s.station_id, code: s.code, name: s.name, point: p }] : [];
    }),
    appliances: live.appliances.flatMap((a) => {
      const p = a.position ? pt(a.position.lon, a.position.lat) : null;
      return p ? [{ id: a.appliance_id, callSign: a.call_sign, status: a.status, point: p, stale: (a.position?.age_seconds ?? 0) > maxAgeS }] : [];
    }),
  };
}

/**
 * The dispatcher's board: the live map (incidents by severity, unhandled alerts, SOS refused outside the area,
 * stations, appliances by status and position), the active incidents with their alert-to-dispatch clocks and flags,
 * and the chosen incident's detail with every dispatch action. Polled every few seconds.
 */
export function BoardScreen() {
  const { principal, focus, refreshMs } = useFireContext();
  const now = useNow();
  const names = useConfigNames();
  const stationName = useStationNames();
  const [selected, setSelected] = useState<string | null>(focus.incidentId ?? null);
  useEffect(() => {
    if (focus.incidentId) setSelected(focus.incidentId);
  }, [focus.incidentId]);
  const live = useApi(["live"], endpoints.getLiveMap, { refetchInterval: refreshMs });
  const wards = useApi(["wards"], endpoints.getWardMap, { enabled: can(principal, "viewWardMap"), staleTime: 10 * 60_000 });
  const maxAge = (names.staff?.policy.eta_position_max_age_minutes ?? 5) * 60;
  const data = useMemo(() => ({ ...liveMapData(live.data, names.incidentType, selected, maxAge), wards: wards.data ?? null }), [live.data, names.incidentType, selected, maxAge, wards.data]);
  const incidents = [...(live.data?.incidents ?? [])].sort(
    (a, b) => SEVERITY_ORDER.indexOf(b.severity as never) - SEVERITY_ORDER.indexOf(a.severity as never) || a.first_alert_at.localeCompare(b.first_alert_at),
  );
  const legend: LegendKey[] = ["incident", "critical", "station", "appliance", "ward"];
  if ((live.data?.alerts.length ?? 0) > 0) legend.push("alert", "sos");
  if ((live.data?.refused_sos.length ?? 0) > 0) legend.push("refused");

  const alerts = live.data?.alerts.length ?? 0;
  const refused = live.data?.refused_sos.length ?? 0;

  return (
    <MapPage
      title="Dispatch board"
      head={
        <>
          {live.data?.as_of && (
            <span className="fer-live" role="status">
              <Dot tone="success" /> Live · updated {formatDateTime(live.data.as_of)}
            </span>
          )}
          {live.data && (
            <p className="fer-hint">
              {alerts} unhandled alert(s) with a location; {refused} SOS refused outside the area.
            </p>
          )}
          <QueryState isLoading={live.isLoading} error={live.error} label="the live map" />
        </>
      }
      map={
        <FireMap
          fill
          fitPadding={{ top: 60, right: selected ? 540 : 100, bottom: 80, left: 440 }}
          ariaLabel="Live map of incidents, alerts, stations and appliances (the same data is in the tables)"
          data={data}
          onPick={(kind, id) => {
            if (kind === "incident") setSelected(id);
          }}
        />
      }
      inspector={selected ? <IncidentDetail key={selected} incidentId={selected} onClose={() => setSelected(null)} /> : null}
      inspectorLabel="Selected incident"
      legend={<Legend keys={legend} />}
    >
      {live.data && (
        <table className="lcc-table lcc-table--compact" aria-label={`Active incidents (${incidents.length})`}>
          <thead>
            <tr>
              <th scope="col">Incident</th>
              <th scope="col" className="fer-num">
                Alert to dispatch
              </th>
            </tr>
          </thead>
          <tbody>
            {incidents.map((i) => {
              const c = alertToDispatch(i, now);
              const isSel = i.incident_id === selected;
              return (
                <tr key={i.incident_id} className={`fer-row--click${isSel ? " fer-row--selected" : ""}`} onClick={() => setSelected(i.incident_id)}>
                  <th scope="row">
                    <span className="fer-cell-stack">
                      <button
                        type="button"
                        className="fer-rowlink"
                        aria-label={`Open incident ${i.reference}`}
                        aria-current={isSel ? "true" : undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(i.incident_id);
                        }}
                      >
                        {i.reference} – {names.incidentType(i.incident_type)}
                      </button>
                      <span className="fer-badges">
                        <StatusBadge status={statusOf(SEVERITY, i.severity)} />
                        <StatusBadge status={statusOf(INCIDENT_STATUS, i.status)} />
                      </span>
                      <IncidentFlags inc={i} />
                    </span>
                  </th>
                  <td className="fer-num fer-clock" role="timer" aria-live="off">
                    {formatSeconds(c.seconds)}
                    {c.running ? " (running)" : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {live.data && incidents.length === 0 && <p className="fer-muted">No active incident.</p>}
      {live.data && (
        <>
          <h3>Appliances</h3>
          <table className="lcc-table lcc-table--compact" aria-label={`Appliances (${live.data.appliances.length})`}>
            <thead>
              <tr>
                <th scope="col">Call sign</th>
                <th scope="col">Status</th>
                <th scope="col">Last position</th>
              </tr>
            </thead>
            <tbody>
              {live.data.appliances.map((a) => (
                <tr key={a.appliance_id}>
                  <th scope="row">
                    {a.call_sign}
                    <span className="fer-sub">{stationName(a.home_station_id)}</span>
                  </th>
                  <td>
                    <span className="fer-cell-stack">
                      <StatusBadge status={statusOf(APPLIANCE_STATUS, a.status)} />
                      {a.dispatch_status && <span className="fer-sub">{statusOf(DISPATCH_STATUS, a.dispatch_status).label}</span>}
                      {a.commander_name && <span className="fer-sub">{a.commander_name}</span>}
                    </span>
                  </td>
                  <td>{a.position ? `${formatSeconds(a.position.age_seconds ?? null)} ago` : <span className="fer-muted">Not shown</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </MapPage>
  );
}
